// Enhanced ShareModal.js with Enterprise Sharing Tab
import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, Calendar, Lock, Share2, Users, Building2, Search, ChevronDown } from 'lucide-react';
import { createShareLink } from '../services/sharing';
import { 
  shareWithEnterpriseUsers, 
  getEnterpriseUsers, 
  isEnterpriseUser,
  validateEmails,
  formatSharingOptions 
} from '../services/enterpriseSharing';

const ShareModal = ({ itemType, itemId, title, versionId, onClose, enableEnterprise = false }) => {
  // General state
  const [activeTab, setActiveTab] = useState('public');
  const [isEnterprise, setIsEnterprise] = useState(false);

  // Public sharing state (existing)
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [expiration, setExpiration] = useState('never');
  const [passwordProtect, setPasswordProtect] = useState(false);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Enterprise sharing state
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [emailSearchInput, setEmailSearchInput] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [enterpriseSharing, setEnterpriseSharing] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState(null);
  const [enterpriseSuccess, setEnterpriseSuccess] = useState(null);
  
  // Refs for dropdown management
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Derived state to control enterprise feature visibility
  const showEnterpriseFeatures = isEnterprise && enableEnterprise;

  // Check if user has enterprise access
  useEffect(() => {
    if (!enableEnterprise) return; // Don't check if the feature is disabled

    const checkEnterprise = async () => {
      try {
        const hasEnterprise = await isEnterpriseUser();
        setIsEnterprise(hasEnterprise);
        
        if (hasEnterprise) {
          // Load enterprise users for search
          const users = await getEnterpriseUsers();
          setEnterpriseUsers(users);
          setFilteredUsers(users);
        }
      } catch (error) {
        console.error('Error checking enterprise access:', error);
        setIsEnterprise(false);
      }
    };

    checkEnterprise();
  }, [enableEnterprise]);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter users based on search input
  useEffect(() => {
    if (!emailSearchInput.trim()) {
      setFilteredUsers(enterpriseUsers);
    } else {
      const searchTerm = emailSearchInput.toLowerCase();
      const filtered = enterpriseUsers.filter(user => 
        user.email.toLowerCase().includes(searchTerm) &&
        !selectedEmails.includes(user.email)
      );
      setFilteredUsers(filtered);
    }
  }, [emailSearchInput, enterpriseUsers, selectedEmails]);

  // Public sharing handlers (existing)
  const handleGenerateLink = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const options = {};
      
      if (expiration !== 'never') {
        options.expirationDays = parseInt(expiration);
      }
      
      if (passwordProtect && password.trim()) {
        options.password = password;
      }
      
      if (itemType === 'view' && versionId) {
        options.versionId = versionId;
      }
      
      const response = await createShareLink(itemType, itemId, options);
      
      if (response.status === 'success') {
        setShareUrl(response.shareUrl);
      } else {
        setError('Failed to generate share link');
      }
    } catch (err) {
      setError('An error occurred while creating the share link');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Enterprise sharing handlers
  const handleEmailSearchChange = (e) => {
    const value = e.target.value;
    setEmailSearchInput(value);
    setShowUserDropdown(true);
    setEnterpriseError(null);
  };

  const handleEmailSearchFocus = () => {
    setShowUserDropdown(true);
  };

  const handleEmailSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Try to find exact match first
      const exactMatch = filteredUsers.find(user => 
        user.email.toLowerCase() === emailSearchInput.toLowerCase().trim()
      );
      
      if (exactMatch) {
        selectEnterpriseUser(exactMatch.email);
      } else {
        // Validate if it's a valid email format and not already selected
        const emailToAdd = emailSearchInput.trim();
        if (emailToAdd && !selectedEmails.includes(emailToAdd)) {
          const { valid } = validateEmails([emailToAdd]);
          if (valid.length > 0) {
            // Check if this email belongs to an enterprise user
            const isEnterpriseEmail = enterpriseUsers.some(user => 
              user.email.toLowerCase() === emailToAdd.toLowerCase()
            );
            
            if (isEnterpriseEmail) {
              selectEnterpriseUser(emailToAdd);
            } else {
              setEnterpriseError('Email address is not part of your enterprise');
            }
          } else {
            setEnterpriseError('Please enter a valid email address');
          }
        }
      }
    } else if (e.key === 'ArrowDown' && filteredUsers.length > 0) {
      e.preventDefault();
      // Focus first dropdown item (you can enhance this with keyboard navigation)
    } else if (e.key === 'Escape') {
      setShowUserDropdown(false);
    }
  };

  const selectEnterpriseUser = (email) => {
    if (!selectedEmails.includes(email)) {
      setSelectedEmails([...selectedEmails, email]);
      setEmailSearchInput('');
      setShowUserDropdown(false);
      setEnterpriseError(null);
    }
  };

  const removeSelectedEmail = (emailToRemove) => {
    setSelectedEmails(selectedEmails.filter(email => email !== emailToRemove));
  };

  const handleEnterpriseShare = async () => {
    if (selectedEmails.length === 0) {
      setEnterpriseError('Please select at least one user to share with');
      return;
    }

    setEnterpriseSharing(true);
    setEnterpriseError(null);
    setEnterpriseSuccess(null);

    try {
      // Enterprise shares always have no expiry and no password
      const options = formatSharingOptions({
        password: null, // Never set password for enterprise shares
        expiration: 'never', // Never set expiration for enterprise shares
        versionId
      });

      const result = await shareWithEnterpriseUsers(
        itemType,
        itemId,
        selectedEmails,
        options
      );

      setEnterpriseSuccess(`Successfully shared with ${result.sharedCount} users`);
      
      // Clear form after successful share
      setTimeout(() => {
        setSelectedEmails([]);
        setEmailSearchInput('');
        onClose();
      }, 2000);

    } catch (error) {
      setEnterpriseError(error.message || 'Failed to share with enterprise users');
    } finally {
      setEnterpriseSharing(false);
    }
  };

  const publicShareContent = (
    <div>
      {!shareUrl ? (
        <>
          <div className="space-y-4 mb-6">
            {/* Expiration Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Expiration
              </label>
              <select
                value={expiration}
                onChange={(e) => setExpiration(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="never">Never expires</option>
                <option value="1">1 day</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            
            {/* Password Protection */}
            <div>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="passwordProtect"
                  checked={passwordProtect}
                  onChange={(e) => setPasswordProtect(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="passwordProtect" className="text-sm font-medium text-gray-700 flex items-center">
                  <Lock className="w-4 h-4 mr-1" />
                  Password protection
                </label>
              </div>
              
              {passwordProtect && (
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full p-2 border rounded"
                />
              )}
            </div>
          </div>
          
          {error && (
            <div className="text-red-500 text-sm mb-4">{error}</div>
          )}
          
          <button
            onClick={handleGenerateLink}
            disabled={isGenerating || (passwordProtect && !password.trim())}
            className="w-full bg-black text-white py-2 px-4 rounded disabled:bg-gray-400"
          >
            {isGenerating ? 'Generating...' : 'Generate Share Link'}
          </button>
        </>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share Link
            </label>
            <div className="flex">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 p-2 border rounded-l bg-gray-50"
              />
              <button
                onClick={handleCopyLink}
                className="bg-black text-white px-4 py-2 rounded-r hover:bg-gray-800 flex items-center"
              >
                <Copy className="w-4 h-4 mr-1" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <button
            onClick={() => {
              setShareUrl('');
              setError(null);
            }}
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            ← Generate New Link
          </button>
        </>
      )}
    </div>
  );

  const enterpriseShareContent = (
    <div>
      {/* Enterprise User Search */}
      <div className="mb-4 relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="w-4 h-4 inline mr-1" />
          Search Enterprise Users
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            ref={searchInputRef}
            type="text"
            value={emailSearchInput}
            onChange={handleEmailSearchChange}
            onFocus={handleEmailSearchFocus}
            onKeyDown={handleEmailSearchKeyPress}
            placeholder="Search by email address..."
            className="w-full pl-10 pr-4 py-2 border rounded focus:ring-2 focus:ring-black focus:border-transparent"
          />
          {showUserDropdown && (
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          )}
        </div>
        
        {/* User Dropdown */}
        {showUserDropdown && filteredUsers.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredUsers.map((user) => (
              <button
                key={user.email}
                onClick={() => selectEnterpriseUser(user.email)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center justify-between"
              >
                <span className="text-sm text-gray-900">{user.email}</span>
                {user.isAdmin && (
                  <span className="text-xs text-blue-600 font-medium">Admin</span>
                )}
              </button>
            ))}
          </div>
        )}
        
        {/* No users found message */}
        {showUserDropdown && emailSearchInput && filteredUsers.length === 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="px-4 py-2 text-sm text-gray-500">
              No enterprise users found matching "{emailSearchInput}"
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500 mt-1">
          Type to search for enterprise users or press Enter to add
        </p>
      </div>

      {/* Selected Users */}
      {selectedEmails.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selected Users ({selectedEmails.length})
          </label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {selectedEmails.map((email) => (
              <div key={email} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                <span className="text-sm text-gray-700">{email}</span>
                <button
                  onClick={() => removeSelectedEmail(email)}
                  className="text-gray-400 hover:text-red-500 ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enterprise Sharing Info */}
<div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
  <p className="text-sm text-blue-800">
    <Lock className="w-4 h-4 inline mr-1" />
    Shared users can view and copy your work but cannot edit the original files. Shares never expire for seamless team collaboration.
  </p>
</div>

      {/* Error/Success Messages */}
      {enterpriseError && (
        <div className="text-red-500 text-sm mb-4 p-3 bg-red-50 border border-red-200 rounded">
          {enterpriseError}
        </div>
      )}
      
      {enterpriseSuccess && (
        <div className="text-green-500 text-sm mb-4 p-3 bg-green-50 border border-green-200 rounded">
          {enterpriseSuccess}
        </div>
      )}

      {/* Share Button */}
      <button
        onClick={handleEnterpriseShare}
        disabled={enterpriseSharing || selectedEmails.length === 0}
        className="w-full bg-black text-white py-2 px-4 rounded disabled:bg-gray-400 flex items-center justify-center"
      >
        {enterpriseSharing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Sharing...
          </>
        ) : (
          <span>Share with {selectedEmails.length} {selectedEmails.length === 1 ? 'User' : 'Users'}</span>
        )}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <Share2 className="w-5 h-5 mr-2 text-gray-700" />
            Share {itemType === 'view' ? 'View' : 'Flow'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 py-2 border-b">
          <p className="text-gray-700">
            Sharing: <span className="font-medium">{title}</span>
          </p>
        </div>

        {/* Tabs - Conditionally Rendered */}
        {showEnterpriseFeatures && (
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('public')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center ${
                activeTab === 'public'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Public Share
            </button>
            <button
              onClick={() => setActiveTab('enterprise')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center ${
                activeTab === 'enterprise'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Enterprise
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {!showEnterpriseFeatures && publicShareContent}
          
          {showEnterpriseFeatures && activeTab === 'public' && publicShareContent}

          {showEnterpriseFeatures && activeTab === 'enterprise' && enterpriseShareContent}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;